import {
  Button,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { useCallback } from "react";
import { OrganMapTemplate } from "../../../services/admin/organMapTemplates.service";
import {
  useOrganMapTemplateLinkedSources,
  useUpdateSubsitePosition,
} from "../../../hooks/admin/useOrganMapTemplates";
import PositionEditor from "./PositionEditor";

interface SubsitePositionsModalProps {
  opened: boolean;
  onClose: () => void;
  template: OrganMapTemplate;
}

export default function SubsitePositionsModal({
  opened,
  onClose,
  template,
}: SubsitePositionsModalProps) {
  const { data: linkedSources = [] } = useOrganMapTemplateLinkedSources(
    template.id
  );
  const updatePosition = useUpdateSubsitePosition();

  const handlePositionUpdate = useCallback(
    (subsiteId: string, positionX: number | null, positionY: number | null) => {
      updatePosition.mutate({
        templateId: template.id,
        subsiteId,
        positionX,
        positionY,
      });
    },
    [template.id, updatePosition]
  );

  const hasImage = !!template.imageUrl;
  const hasLinkedSources = linkedSources.length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Subsite Positions: ${template.name}`}
      size={1100}
      closeOnClickOutside={false}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          maxHeight: "calc(100vh - 200px)",
        },
      }}
    >
      <ScrollArea flex={1} px="md" pt="md">
        <Stack gap="md">
          {!hasImage && (
            <Text size="sm" c="dimmed">
              This template has no image uploaded. Upload an image first to
              position subsites.
            </Text>
          )}
          {hasImage && !hasLinkedSources && (
            <Text size="sm" c="dimmed">
              No sources are linked to this template. Link sources first to see
              available subsites for positioning.
            </Text>
          )}
          {hasImage && hasLinkedSources && (
            <PositionEditor
              imageUrl={template.imageUrl || null}
              width={template.width || null}
              height={template.height || null}
              linkedSources={linkedSources}
              onPositionUpdate={handlePositionUpdate}
            />
          )}
        </Stack>
      </ScrollArea>
      <Divider />
      <Group justify="flex-end" p="md">
        <Button variant="default" onClick={onClose}>
          Close
        </Button>
      </Group>
    </Modal>
  );
}
